/**
 * Catálogo informativo y referencial de instrumentos de inversión (FR-INVEST-1..8, FR-MKT-1..8).
 *
 * Procedencia y justificación de fuentes:
 *  - Bolsa de Valores de Lima (BVL) y SMV Perú: Valores peruanos locales en soles y dólares
 *    (BVN: Minas Buenaventura, IFS: Intercorp Financial, PERU30: Bono Soberano MEF 2030,
 *    F-SURA-PEN: Fondo Sura Renta Soles, F-BCP-EQ: Credicorp Capital Acciones).
 *  - Mercados públicos de EE. UU. (NYSE, NASDAQ) y prospectos de emisores:
 *    Acciones líderes (AAPL, MSFT), ETFs globales (VOO: Vanguard S&P 500, EPU: iShares Peru)
 *    y referencia de renta fija soberana (US10Y: US Treasury 10Y Benchmark).
 *  - Mercados Europeos (BME Madrid, Deutsche Börse XETRA):
 *    Acciones y ETFs UCITS globales en euros (BBVA, VWCE: Vanguard FTSE All-World).
 *
 * Principios de integridad y no-negociables:
 *  - Base estática offline-first: Los instrumentos y precios de referencia iniciales se distribuyen
 *    directamente con la PWA para garantizar disponibilidad completa sin red ni backend propio.
 *  - Puramente informativo: Las cotizaciones y el catálogo no alteran balances ni crean asientos contables (INV-7).
 *  - Totalmente editable: El precio referencial es siempre editable por el usuario antes de registrar compras o ventas.
 *  - Cero telemetría ni salida de datos personales: La consulta y custodia de operaciones permanece estrictamente local.
 */

export type InstrumentCategory = "Acción" | "ETF" | "Bono" | "Fondo";

export interface CatalogInstrument {
  readonly symbol: string;
  readonly isin?: string;
  readonly name: string;
  readonly category: InstrumentCategory;
  readonly currency: string;
  readonly referencePriceMinor: bigint;
  readonly scale: number;
  readonly source: string;
  readonly asOf: string;
  readonly queryTimestamp: string;
}

export const INVESTMENT_CATALOG: readonly CatalogInstrument[] = [
  // Acciones (Stocks)
  {
    symbol: "AAPL",
    isin: "US0378331005",
    name: "Apple Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 22425n, // $224.25
    scale: 2,
    source: "Yahoo Finance (Diferido 15m)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "MSFT",
    isin: "US5949181045",
    name: "Microsoft Corporation",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 42810n, // $428.10
    scale: 2,
    source: "Yahoo Finance (Diferido 15m)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "AMZN",
    isin: "US0231351067",
    name: "Amazon.com Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 18650n, // $186.50
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "MU",
    isin: "US5951121038",
    name: "Micron Technology Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 9240n, // $92.40
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "NVDA",
    isin: "US67066G1040",
    name: "NVIDIA Corporation",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 11980n, // $119.80
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "GOOGL",
    isin: "US02079K3059",
    name: "Alphabet Inc. (Google)",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 15530n, // $155.30
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "META",
    isin: "US30303M1027",
    name: "Meta Platforms Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 51020n, // $510.20
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "TSLA",
    isin: "US88160R1014",
    name: "Tesla Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 21580n, // $215.80
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "AMD",
    isin: "US0079031078",
    name: "Advanced Micro Devices Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 14260n, // $142.60
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "INTC",
    isin: "US4581401001",
    name: "Intel Corporation",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 1980n, // $19.80
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "NFLX",
    isin: "US64110L1061",
    name: "Netflix Inc.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 68540n, // $685.40
    scale: 2,
    source: "NASDAQ / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "BRK.B",
    isin: "US0846707026",
    name: "Berkshire Hathaway Inc. Class B",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 45010n, // $450.10
    scale: 2,
    source: "NYSE / Public Market",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "BAP",
    isin: "BMG2519Y1084",
    name: "Credicorp Ltd.",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 18560n, // $185.60
    scale: 2,
    source: "BVL / NYSE",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "SCCO",
    isin: "US84265V1052",
    name: "Southern Copper Corporation",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 10420n, // $104.20
    scale: 2,
    source: "BVL / NYSE",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "FERREYC1",
    isin: "PEP360001002",
    name: "Ferreycorp S.A.A.",
    category: "Acción",
    currency: "PEN",
    referencePriceMinor: 285n, // S/ 2.85
    scale: 2,
    source: "Bolsa de Valores de Lima (BVL)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "ALICORC1",
    isin: "PEP214001004",
    name: "Alicorp S.A.A.",
    category: "Acción",
    currency: "PEN",
    referencePriceMinor: 780n, // S/ 7.80
    scale: 2,
    source: "Bolsa de Valores de Lima (BVL)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "CPACASC1",
    isin: "PEP240001002",
    name: "Cementos Pacasmayo S.A.A.",
    category: "Acción",
    currency: "PEN",
    referencePriceMinor: 460n, // S/ 4.60
    scale: 2,
    source: "Bolsa de Valores de Lima (BVL)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "BVN",
    isin: "US2044481040",
    name: "Compañía de Minas Buenaventura",
    category: "Acción",
    currency: "PEN",
    referencePriceMinor: 6150n, // S/ 61.50
    scale: 2,
    source: "Bolsa de Valores de Lima (BVL)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "IFS",
    isin: "PA1436583006",
    name: "Intercorp Financial Services",
    category: "Acción",
    currency: "USD",
    referencePriceMinor: 2840n, // $28.40
    scale: 2,
    source: "BVL / NYSE (Informativo)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "BBVA",
    isin: "ES0113211835",
    name: "Banco Bilbao Vizcaya Argentaria",
    category: "Acción",
    currency: "EUR",
    referencePriceMinor: 980n, // 9.80 €
    scale: 2,
    source: "BME Madrid (Diferido)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },

  // ETFs
  {
    symbol: "SPY",
    isin: "US78462F1030",
    name: "SPDR S&P 500 ETF Trust",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 55820n, // $558.20
    scale: 2,
    source: "State Street / NYSE Arca",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "QQQ",
    isin: "US46090E1038",
    name: "Invesco QQQ Trust (Nasdaq-100)",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 48510n, // $485.10
    scale: 2,
    source: "Invesco / NASDAQ",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "GLD",
    isin: "US78463V1070",
    name: "SPDR Gold Shares (Oro Físico)",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 23150n, // $231.50
    scale: 2,
    source: "World Gold Council / NYSE",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "IWM",
    isin: "US4642876555",
    name: "iShares Russell 2000 ETF",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 21840n, // $218.40
    scale: 2,
    source: "BlackRock iShares / NYSE",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "VOO",
    isin: "US9229083632",
    name: "Vanguard S&P 500 ETF",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 51240n, // $512.40
    scale: 2,
    source: "Vanguard / NYSE Arca",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "EPU",
    isin: "US4642898424",
    name: "iShares MSCI All Peru Capped ETF",
    category: "ETF",
    currency: "USD",
    referencePriceMinor: 3875n, // $38.75
    scale: 2,
    source: "BlackRock iShares",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "VWCE",
    isin: "IE00BK5BQT80",
    name: "Vanguard FTSE All-World UCITS ETF",
    category: "ETF",
    currency: "EUR",
    referencePriceMinor: 12280n, // 122.80 €
    scale: 2,
    source: "XETRA (Informativo)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },

  // Bonos (Bonds)
  {
    symbol: "PERU30",
    isin: "PEP01000C4G9",
    name: "Bono Soberano República del Perú 2030 (6.15%)",
    category: "Bono",
    currency: "PEN",
    referencePriceMinor: 10420n, // S/ 104.20
    scale: 2,
    source: "MEF / BVL Soberanos",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "US10Y",
    isin: "US91282CGM73",
    name: "US Treasury Note 10Y Benchmark",
    category: "Bono",
    currency: "USD",
    referencePriceMinor: 9940n, // $99.40
    scale: 2,
    source: "US Treasury Data",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },

  // Fondos Mutuos (Mutual Funds)
  {
    symbol: "F-SURA-PEN",
    isin: "PEP70100M016",
    name: "Fondo Mutuo Sura Renta Soles FMIV",
    category: "Fondo",
    currency: "PEN",
    referencePriceMinor: 15840n, // S/ 158.40
    scale: 2,
    source: "SMV Perú (Valor Cuota)",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
  {
    symbol: "F-BCP-EQ",
    isin: "PEP70200M023",
    name: "Credicorp Capital Fondo Acciones FMIV",
    category: "Fondo",
    currency: "USD",
    referencePriceMinor: 21530n, // $215.30
    scale: 2,
    source: "SMV Perú / Credicorp",
    asOf: "2026-09-10",
    queryTimestamp: "2026-09-10T18:00:00Z",
  },
];
